// Unified Virtual Room Card Component
// Single card with category dropdown and action buttons

import React, { useState } from 'react';

interface UnifiedVirtualRoomCardProps {
  onLaunchRoom: (category: string) => void;
  onCopyLink: (category: string) => void;
  onSchedule: (category: string) => void;
  onAgenda: (category: string) => void;
  onAddToCalendar: (category: string) => void;
  playSound: (type: 'click' | 'success' | 'notification') => void;
}

type RoomCategory = 'discussion' | 'assignment' | 'experiential' | 'social';

interface CategoryInfo {
  id: RoomCategory;
  name: string;
  icon: string;
  color: string;
  borderColor: string;
  bgColor: string;
  hoverColor: string;
}

const CATEGORIES: CategoryInfo[] = [
  {
    id: 'discussion',
    name: 'Discussion',
    icon: '💬',
    color: 'text-blue-300',
    borderColor: 'border-blue-400/40',
    bgColor: 'bg-blue-600/15',
    hoverColor: 'hover:bg-blue-600/25',
  },
  {
    id: 'assignment',
    name: 'Assignment',
    icon: '📝',
    color: 'text-amber-300',
    borderColor: 'border-amber-400/40',
    bgColor: 'bg-amber-600/15',
    hoverColor: 'hover:bg-amber-600/25',
  },
  {
    id: 'experiential',
    name: 'Experiential',
    icon: '🧪',
    color: 'text-emerald-300',
    borderColor: 'border-emerald-400/40',
    bgColor: 'bg-emerald-600/15',
    hoverColor: 'hover:bg-emerald-600/25',
  },
  {
    id: 'social',
    name: 'Social/Family',
    icon: '👥',
    color: 'text-pink-300',
    borderColor: 'border-pink-400/40',
    bgColor: 'bg-pink-600/15',
    hoverColor: 'hover:bg-pink-600/25',
  },
];

export const UnifiedVirtualRoomCard: React.FC<UnifiedVirtualRoomCardProps> = ({
  onLaunchRoom,
  onCopyLink,
  onSchedule,
  onAgenda,
  onAddToCalendar,
  playSound,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<CategoryInfo>(CATEGORIES[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleCategorySelect = (category: CategoryInfo) => {
    playSound('click');
    setSelectedCategory(category);
    setIsDropdownOpen(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-3xl">🎥</span>
        <h3 className="text-lg font-bold text-white tracking-wide">Virtual Room</h3>
      </div>
      
      <div className={`border ${selectedCategory.borderColor} ${selectedCategory.bgColor} backdrop-blur-xl rounded-2xl shadow-[0_8px_32px_0_rgba(168,85,247,0.2)] overflow-hidden relative`}>
        {/* Glass shine effect */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        
        {/* Category Selector */}
        <div className="p-4 border-b border-white/10 bg-gradient-to-b from-white/5 to-transparent">
          <div className="text-xs uppercase tracking-wider text-white/60 mb-2">Meeting Type</div>
          <div className="relative">
            <button
              onClick={() => {
                playSound('click');
                setIsDropdownOpen(!isDropdownOpen);
              }}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 ${selectedCategory.borderColor} ${selectedCategory.bgColor} ${selectedCategory.hoverColor} backdrop-blur-sm transition-all shadow-lg`}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{selectedCategory.icon}</span>
                <span className={`text-sm font-bold ${selectedCategory.color}`}>{selectedCategory.name}</span>
              </div>
              <span className={`text-xs ${selectedCategory.color} transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {/* Dropdown Menu - Glass morphism */}
            {isDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-[#0a0612]/95 backdrop-blur-xl border-2 border-purple-400/30 rounded-xl shadow-2xl z-50 overflow-hidden">
                {CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category)}
                    className={`w-full flex items-center gap-3 px-4 py-3 ${category.bgColor} ${category.hoverColor} backdrop-blur-sm transition-all border-b border-white/5 last:border-b-0`}
                  >
                    <span className="text-xl">{category.icon}</span>
                    <span className={`text-sm font-semibold ${category.color}`}>{category.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Launch Button - Glass effect */}
        <button
          onClick={() => {
            playSound('success');
            onLaunchRoom(selectedCategory.id);
          }}
          className={`w-full flex items-center justify-between px-4 py-4 ${selectedCategory.hoverColor} backdrop-blur-sm transition-all border-b border-white/10 bg-gradient-to-r from-white/5 to-transparent`}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎥</span>
            <div className="flex flex-col items-start">
              <span className="text-base font-bold text-white">Launch Meeting</span>
              <span className="text-xs text-white/50">Start video conference</span>
            </div>
          </div>
          <span className="text-xs text-white/70 uppercase tracking-wider">Open →</span>
        </button>

        {/* Action Buttons - Glass panels */}
        <div className="px-4 py-4 space-y-2 bg-gradient-to-b from-transparent to-black/20 backdrop-blur-sm">
          <button
            onClick={() => {
              playSound('click');
              onCopyLink(selectedCategory.id);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-cyan-400/40 bg-cyan-600/10 hover:bg-cyan-600/20 backdrop-blur-sm transition-all shadow-lg hover:shadow-cyan-500/20"
          >
            <span className="text-lg">🔗</span>
            <div className="flex-1 text-left">
              <div className="text-sm font-bold text-cyan-300">Copy Link</div>
              <div className="text-xs text-cyan-200/60">Share meeting URL</div>
            </div>
            <span className="text-cyan-300">→</span>
          </button>

          <button
            onClick={() => {
              playSound('click');
              onSchedule(selectedCategory.id);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-purple-400/40 bg-purple-600/10 hover:bg-purple-600/20 backdrop-blur-sm transition-all shadow-lg hover:shadow-purple-500/20"
          >
            <span className="text-lg">📅</span>
            <div className="flex-1 text-left">
              <div className="text-sm font-bold text-purple-300">Schedule</div>
              <div className="text-xs text-purple-200/60">Set date & time</div>
            </div>
            <span className="text-purple-300">→</span>
          </button>

          <button
            onClick={() => {
              playSound('click');
              onAgenda(selectedCategory.id);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-emerald-400/40 bg-emerald-600/10 hover:bg-emerald-600/20 backdrop-blur-sm transition-all shadow-lg hover:shadow-emerald-500/20"
          >
            <span className="text-lg">📋</span>
            <div className="flex-1 text-left">
              <div className="text-sm font-bold text-emerald-300">Create Agenda</div>
              <div className="text-xs text-emerald-200/60">Plan discussion points</div>
            </div>
            <span className="text-emerald-300">→</span>
          </button>

          <button
            onClick={() => {
              playSound('click');
              onAddToCalendar(selectedCategory.id);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-pink-400/40 bg-pink-600/10 hover:bg-pink-600/20 backdrop-blur-sm transition-all shadow-lg hover:shadow-pink-500/20"
          >
            <span className="text-lg">📆</span>
            <div className="flex-1 text-left">
              <div className="text-sm font-bold text-pink-300">Add to Calendar</div>
              <div className="text-xs text-pink-200/60">Google/Apple/Outlook</div>
            </div>
            <span className="text-pink-300">→</span>
          </button>
        </div>

        {/* Notes Destination Badge - Glass panel */}
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-black/30 backdrop-blur-sm border border-white/10 shadow-inner">
            <span className="text-sm">💡</span>
            <span className="text-xs text-white/60">
              Notes will save to <span className="font-bold text-white">{selectedCategory.name}</span> folder
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
